import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { formatDateTime } from "../utils/recurrence";

// App-wide notification bell, mirroring Android's TopBarInboxBell: a bell with
// an unread badge that opens a dropdown of the (synced) inbox feed. Drop it into
// any screen's TopBar.
const NotificationBell = () => {
  const navigate = useNavigate();
  const {
    notifications,
    projects,
    unreadNotificationCount,
    dismissNotification,
  } = useContext(FirebaseContext);

  // Resolve the owning project's name (and color, if any) for a notification.
  const projectFor = (entry) =>
    projects.find((p) => Number(p.id) === Number(entry.projectId)) || null;
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const visible = open || closing;

  // Animate the drawer out before unmounting (matches the slide-in on open).
  const requestClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setOpen(false);
    }, 200);
  };

  // Tapping a notification dismisses it (removes from feed) and navigates.
  const openTask = (entry) => {
    dismissNotification(entry);
    setOpen(false);
    setClosing(false);
    if (entry.taskId != null) navigate(`/taskDetail/${entry.taskId}`);
  };

  return (
    <div style={styles.wrap}>
      <button
        style={styles.bellButton}
        onClick={() => (open ? requestClose() : setOpen(true))}
        aria-label="Notifications"
      >
        <BellIcon />
        {unreadNotificationCount > 0 && <span style={styles.dotBadge} />}
      </button>

      {visible && (
        <>
          <div
            style={{
              ...styles.overlay,
              animation: closing
                ? "atInboxFadeOut 200ms ease-in forwards"
                : "atInboxFadeIn 200ms ease-out",
            }}
            onClick={requestClose}
          />
          <div
            style={{
              ...styles.drawer,
              animation: closing
                ? "atInboxSlideOut 200ms ease-in forwards"
                : "atInboxSlideIn 200ms ease-out",
            }}
          >
            <div style={styles.header}>
              <span style={styles.headerTitle}>Inbox</span>
            </div>
            <div style={styles.divider} />
            {notifications.length === 0 && (
              <div style={styles.empty}>No notifications yet</div>
            )}
            <div style={styles.list}>
              {notifications.map((entry) => {
                const project = projectFor(entry);
                return (
                  <div key={entry.id} style={styles.row}>
                    {/* Unread cue: filled primary dot when unread, hollow when read.
                        Kept the same footprint either way so titles stay aligned. */}
                    <span
                      style={{
                        ...styles.unreadDot,
                        background: entry.read ? "transparent" : "var(--primary, #007bff)",
                        border: entry.read ? "1.5px solid rgba(128,128,128,0.45)" : "none",
                      }}
                    />
                    <div style={styles.rowBody} onClick={() => openTask(entry)}>
                      <div style={{ ...styles.rowTitle, fontWeight: entry.read ? 500 : 700 }}>
                        {entry.title || "Notification"}
                      </div>
                      <div style={styles.rowMeta}>
                        {project && (
                          <span style={styles.projectName}>
                            {project.projectName || "Untitled"}
                          </span>
                        )}
                        {project && <span style={styles.metaSep}>·</span>}
                        <span style={styles.rowTime}>{formatDateTime(entry.firedAt)}</span>
                      </div>
                    </div>
                    <button
                      style={styles.dismiss}
                      onClick={() => dismissNotification(entry)}
                      aria-label="Dismiss"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const BellIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const DANGER = "#e5484d";

const styles = {
  wrap: { position: "relative", display: "inline-flex", alignItems: "center" },
  bellButton: {
    position: "relative",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--text)",
    padding: "4px",
    display: "inline-flex",
    alignItems: "center",
  },
  dotBadge: {
    position: "absolute",
    top: "1px",
    right: "1px",
    width: "9px",
    height: "9px",
    background: DANGER,
    borderRadius: "50%",
    border: "1.5px solid var(--surface)",
    boxSizing: "border-box",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 1190,
  },
  drawer: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    width: "320px",
    maxWidth: "85vw",
    background: "var(--surface)",
    color: "var(--text)",
    boxShadow: "2px 0 12px rgba(0,0,0,0.35)",
    zIndex: 1200,
    padding: "16px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: { fontWeight: 700, fontSize: "18px" },
  divider: {
    height: "1px",
    background: "rgba(128,128,128,0.3)",
    margin: "8px 0",
  },
  empty: { color: "var(--text-muted, #888)", fontSize: "14px", padding: "8px 0" },
  list: { overflowY: "auto", flex: 1 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 0",
    borderBottom: "1px solid rgba(128,128,128,0.2)",
  },
  unreadDot: {
    width: "9px",
    height: "9px",
    borderRadius: "50%",
    flexShrink: 0,
    boxSizing: "border-box",
    alignSelf: "flex-start",
    marginTop: "5px",
  },
  rowBody: { flex: 1, cursor: "pointer", minWidth: 0 },
  rowTitle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowMeta: { display: "flex", alignItems: "center", gap: "5px", marginTop: "2px", minWidth: 0 },
  projectName: {
    fontSize: "11px",
    fontWeight: 600,
    color: "var(--primary, #007bff)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "55%",
  },
  metaSep: { fontSize: "11px", color: "var(--text-muted, #888)" },
  rowTime: { fontSize: "11px", color: "var(--text-muted, #888)" },
  dismiss: {
    background: "none",
    border: "none",
    color: "var(--text-muted, #888)",
    cursor: "pointer",
    fontSize: "14px",
  },
};

export default NotificationBell;
